import React from 'react';
import { TimePicker as AntTimePicker } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';
import { useTheme } from 'src/theming/useTheme';

const { RangePicker } = AntTimePicker;

export type TimePickerVariant = 'default' | 'secondary';

export type TimePickerProps = React.ComponentPropsWithoutRef<typeof AntTimePicker> &
  Record<string, any> & {
    variant?: TimePickerVariant;
    className?: string;
    dropdownClassName?: string;
    status?: 'error' | 'warning';
  };

export type TimeRangePickerProps = React.ComponentPropsWithoutRef<typeof RangePicker> &
  Record<string, any> & {
    variant?: TimePickerVariant;
    className?: string;
    dropdownClassName?: string;
    status?: 'error' | 'warning';
  };

interface PickerStylesParams {
  variant?: TimePickerVariant;
  status?: 'error' | 'warning';
  className?: string;
  dropdownClassName?: string;
}

interface PickerStylesReturn {
  combinedClassName: string;
  combinedDropdownClassName: string;
}

const useStyles = createUseStyles((theme: ThemeType) => {
  const createTimePickerStyles = (variant: TimePickerVariant = 'default') => {
    const bg = variant === 'default' ? theme.color.semantic.background.neutral.secondary : theme.color.semantic.brand.default.muted;
    const border = variant === 'default' ? theme.color.semantic.border.primary : theme.color.semantic.brand.default.subtle;

    return {
      width: '100%',
      height: '32px',
      backgroundColor: `${bg} !important`,
      borderColor: `${border} !important`,
      borderWidth: theme.border.width.sm,
      borderRadius: `${theme.radius.sm} !important`,
      padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
      transition: 'all 0.3s',

      '& .ant-picker-input > input': {
        color: theme.color.semantic.text.primary,
        fontSize: theme.typography.fontSize.sm,
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
      },
      '& .ant-picker-suffix': {
        color: theme.color.semantic.text.soft,
      },
      '& .ant-picker-clear': {
        backgroundColor: bg,
        color: theme.color.semantic.text.soft,
        '&:hover': {
          color: theme.color.semantic.text.primary,
        },
      },
      '& .ant-picker-separator': {
        color: theme.color.semantic.text.soft,
      },
      '& .ant-picker-active-bar': {
        backgroundColor: theme.color.semantic.brand.default.primary,
      },

      // Hover state
      '&:hover': {
        backgroundColor: `${bg} !important`,
        borderColor: `${theme.color.semantic.brand.default.soft} !important`,
      },

      // Focus state
      '&.ant-picker-focused': {
        backgroundColor: `${bg} !important`,
        borderColor: `${theme.color.semantic.brand.default.moderate} !important`,
        boxShadow: 'none !important',
      },

      // Disabled state
      '&.ant-picker-disabled': {
        backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        opacity: `${theme.opacity[70]} !important`,
        borderColor: `${theme.color.semantic.border.primary} !important`,
        cursor: 'not-allowed',
        '& .ant-picker-input > input': {
          color: theme.color.semantic.text.soft,
          cursor: 'not-allowed',
          '&::placeholder': {
            color: theme.color.semantic.text.soft,
          },
        },
        '& .ant-picker-suffix': {
          color: theme.color.semantic.text.soft,
        },
      },

      // Size variants - Small
      '&.ant-picker-small': {
        height: '24px',
        padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
        fontSize: theme.typography.fontSize.sm,
        '& .ant-picker-input > input': {
          fontSize: theme.typography.fontSize.sm,
        },
      },

      // Size variants - Large
      '&.ant-picker-large': {
        height: '40px',
        padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
        fontSize: theme.typography.fontSize.lg,
        '& .ant-picker-input > input': {
          fontSize: theme.typography.fontSize.lg,
        },
      },

      // Status error
      '&.timepicker-status-error': {
        borderColor: `${theme.color.semantic.brand.danger.light} !important`,
        '&:hover': {
          borderColor: `${theme.color.semantic.brand.danger.light} !important`,
        },
        '&.ant-picker-focused': {
          borderColor: `${theme.color.semantic.brand.danger.light} !important`,
        },
      },

      // Status warning
      '&.timepicker-status-warning': {
        borderColor: `${theme.color.semantic.accent.orange.strong} !important`,
        '&:hover': {
          borderColor: `${theme.color.semantic.accent.orange.strong} !important`,
        },
        '&.ant-picker-focused': {
          borderColor: `${theme.color.semantic.accent.orange.strong} !important`,
        },
      },
    };
  };

  const createDropdownStyles = (variant: TimePickerVariant = 'default') => {
    const bg = variant === 'default' ? theme.color.semantic.background.neutral.secondary : theme.color.semantic.brand.default.muted;
    const border = variant === 'default' ? theme.color.semantic.border.primary : theme.color.semantic.brand.default.subtle;

    return {
      '& .ant-picker-panel-container': {
        backgroundColor: `${bg} !important`,
        borderRadius: `${theme.radius.md} !important`,
        boxShadow: `${theme.shadow.md} !important`,
      },
      '& .ant-picker-panel': {
        backgroundColor: `${bg} !important`,
        borderColor: `${border} !important`,
      },
      '& .ant-picker-header': {
        color: `${theme.color.semantic.text.primary} !important`,
        borderBottomColor: `${border} !important`,
      },
      '& .ant-picker-header button': {
        color: `${theme.color.semantic.text.soft} !important`,
      },
      '& .ant-picker-header button:hover': {
        color: `${theme.color.semantic.text.primary} !important`,
      },
      '& .ant-picker-time-panel': {
        backgroundColor: `${bg} !important`,
        borderLeftColor: `${border} !important`,
      },
      '& .ant-picker-time-panel-column': {
        backgroundColor: `${bg} !important`,
      },
      '& .ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner': {
        color: `${theme.color.semantic.text.primary} !important`,
        borderRadius: `${theme.radius.sm} !important`,
      },
      '& .ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner:hover': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
      },
      '& .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner': {
        backgroundColor: `${bg} !important`,
        color: `${theme.color.semantic.text.primary} !important`,
      },
      '& .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-disabled .ant-picker-time-panel-cell-inner': {
        color: `${theme.color.semantic.text.soft} !important`,
        cursor: 'not-allowed !important',
      },
      '& .ant-picker-footer': {
        borderTopColor: `${border} !important`,
        backgroundColor: `${bg} !important`,
      },
      '& .ant-picker-now-btn': {
        color: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '& .ant-picker-now-btn:hover': {
        color: `${theme.color.semantic.brand.default.soft} !important`,
      },
      '& .ant-picker-ok button': {
        backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
      },
      '& .ant-picker-ok button:hover': {
        backgroundColor: `${theme.color.semantic.brand.default.soft} !important`,
        borderColor: `${theme.color.semantic.brand.default.soft} !important`,
      },
    };
  };

  return {
    defaultTimePicker: createTimePickerStyles('default'),
    secondaryTimePicker: createTimePickerStyles('secondary'),
    defaultTimePickerDropdown: createDropdownStyles('default'),
    secondaryTimePickerDropdown: createDropdownStyles('secondary'),
  };
});

const usePickerStyles = ({
  variant = 'default',
  status,
  className = '',
  dropdownClassName = '',
}: PickerStylesParams): PickerStylesReturn => {
  const classes = useStyles();

  const variantClass = variant === 'secondary' ? classes.secondaryTimePicker : classes.defaultTimePicker;
  const statusClass = status ? `timepicker-status-${status}` : '';
  const combinedClassName = `${variantClass} ${statusClass} ${className}`.trim();

  const dropdownVariantClass = variant === 'secondary'
    ? classes.secondaryTimePickerDropdown
    : classes.defaultTimePickerDropdown;
  const combinedDropdownClassName = `${dropdownVariantClass} ${dropdownClassName}`.trim();

  return { combinedClassName, combinedDropdownClassName };
};

const TimePickerComponent: React.FC<TimePickerProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  dropdownClassName = '',
  ...rest
}) => {
  const { combinedClassName, combinedDropdownClassName } = usePickerStyles({
    variant,
    status,
    className,
    dropdownClassName,
  });

  return (
    <AntTimePicker
      size={size}
      {...rest}
      className={combinedClassName}
      dropdownClassName={combinedDropdownClassName}
    />
  );
};

export const TimeRangePicker: React.FC<TimeRangePickerProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  dropdownClassName = '',
  ...rest
}) => {
  const { combinedClassName, combinedDropdownClassName } = usePickerStyles({
    variant,
    status,
    className,
    dropdownClassName,
  });

  return (
    <RangePicker
      size={size}
      className={combinedClassName}
      dropdownClassName={combinedDropdownClassName}
      {...rest}
    />
  );
};

export const TimePicker = TimePickerComponent as typeof TimePickerComponent & {
  RangePicker: typeof TimeRangePicker;
};

TimePicker.RangePicker = TimeRangePicker;

export default TimePicker;